import { Box, Card, CardContent, CardHeader, Chip, Collapse, Paper, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { truckModeEnum } from '../../constants';
import { getMaterialStateColor } from '../../constants/colors';
import { isTruckContextEnabled } from '../../constants/helpers/truck';
import { getTruckModeLabel } from '../../constants/labels';
import { TruckSupervision } from '../../services/apiSLTP/materials/trucks';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { formatNumber } from '../../utils/numbers';
import Icon from '../ui/Icon';
import IconButton from '../ui/IconButton';
import { formatTime } from '../../utils/dates';

interface TruckCardSupervisionProps {
    truck: TruckSupervision;
}

const TruckCardSupervision: React.FC<TruckCardSupervisionProps> = ({ truck }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const totalDeparture = truck.rides.reduce((acc, r) => acc + (r.departureTonnage || 0), 0);
    const totalArrival = truck.rides.reduce((acc, r) => acc + (r.arrivalTonnage || 0), 0);

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setExpanded(!expanded);
    };

    return (
        <Card sx={{ mb: 1 }}>
            <CardHeader
                avatar={
                    <IconButton
                        onClick={handleExpandClick}
                        iconName="keyboardArrowRightSharp"
                        sx={{
                            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                        }}
                    />
                }
                title={
                    <Typography variant="h6" component="div">
                        {truck.truck?.designation}
                    </Typography>
                }
                subheader={
                    <>
                        <Typography variant="body2" color="textSecondary">
                            {truck.truck?.plateNumber} - {truck.truck?.truckTypeId?.designation}
                        </Typography>
                    </>
                }
                action={
                    <Stack direction="row" spacing={3}>
                        <Stack direction="row" spacing={1}>
                            <Box textAlign="center" width={40}>
                                <Typography variant="h5" color="primary">
                                    {truck.rides.filter((r) => r.mode === truckModeEnum.APPRO).length}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Appro
                                </Typography>
                            </Box>

                            <Box textAlign="center" width={100}>
                                <Typography variant="h5" sx={{ color: '#00b8c2' }}>
                                    {formatNumber(totalDeparture, t('locale'), 2)} t
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Tonnage Appro
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Box textAlign="center" width={40}>
                                <Typography variant="h5" color="primary">
                                    {truck.rides.filter((r) => r.mode === truckModeEnum.EVAC).length}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Evac
                                </Typography>
                            </Box>

                            <Box textAlign="center" width={100}>
                                <Typography variant="h5" sx={{ color: 'lightgreen' }}>
                                    {formatNumber(totalArrival, t('locale'), 2)} t
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Tonnage Evac
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>
                }
                sx={{ cursor: 'pointer' }}
                onClick={handleExpandClick}
            />

            <Stack direction={'column'} sx={{ pl: 8, pb: 2 }}>
                {truck.rides
                    .map((r) => r.activitySite?.name)
                    .filter((name, i, arr) => name && arr.indexOf(name) === i)
                    .map((siteName, index) => (
                        <Typography key={index} variant="caption" color="textSecondary">
                            {siteName}
                        </Typography>
                    ))}
            </Stack>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent sx={{ pt: 0 }}>
                    <Box display={'flex'} flexDirection="column" gap={2}>
                        {truck.rides
                            .sort((a, b) => {
                                const timeA = a.time ? new Date(a.time).getTime() : 0;
                                const timeB = b.time ? new Date(b.time).getTime() : 0;
                                return timeA - timeB;
                            })
                            .map((ride, index) => (
                                <Paper
                                    key={ride._id}
                                    elevation={3}
                                    sx={{
                                        p: 1,
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            boxShadow: 3,
                                            transform: 'translateX(4px)',
                                        },
                                        gap: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip
                                                label={`#${index + 1} - ${capitalizeFirstLetter(getTruckModeLabel(ride.mode))}`}
                                                size="small"
                                                color="default"
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                        </Stack>
                                        <Stack direction="row" spacing={3} justifyContent="flex-end">
                                            <Box textAlign="center">
                                                {!!ride.departureTonnage && (
                                                    <Typography variant="h6" sx={{ color: '#00b8c2', fontWeight: 'bold' }}>
                                                        {formatNumber(ride.departureTonnage || 0, t('locale'), 2)} t
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box textAlign="center">
                                                {!!ride.arrivalTonnage && (
                                                    <Typography variant="h6" sx={{ color: 'lightgreen', fontWeight: 'bold' }}>
                                                        {formatNumber(ride.arrivalTonnage || 0, t('locale'), 2)} t
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            {isTruckContextEnabled(ride.mode, 'departure') && (
                                                <>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {ride.departureTime ? formatTime(new Date(ride.departureTime)) : '-'}
                                                        {' → '}
                                                    </Typography>
                                                </>
                                            )}
                                            {ride.time && (
                                                <Chip
                                                    icon={<Icon name="place" />}
                                                    label={formatTime(new Date(ride.time))}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            )}
                                            {isTruckContextEnabled(ride.mode, 'arrival') && (
                                                <>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {' → '}
                                                        {ride.arrivalTime ? formatTime(new Date(ride.arrivalTime)) : '-'}
                                                    </Typography>
                                                </>
                                            )}
                                            {isTruckContextEnabled(ride.mode, 'secondTimeOnSite') && (
                                                <>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {' → '}
                                                        {ride.secondTime ? formatTime(new Date(ride.secondTime)) : '-'}
                                                    </Typography>
                                                </>
                                            )}
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            {isTruckContextEnabled(ride.mode, 'departure') && (
                                                <Typography variant="body2" fontWeight="medium">
                                                    {ride.departureSiteId?.name || ride.departureQuarryId?.designation || '-'}
                                                    {' → '}
                                                </Typography>
                                            )}

                                            <Chip
                                                icon={<Icon name="place" />}
                                                label={ride.activitySite.name}
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                            />
                                            {isTruckContextEnabled(ride.mode, 'arrival') && (
                                                <Typography variant="body2" fontWeight="medium">
                                                    {' → '}
                                                    {ride.arrivalSiteId?.name || ride.arrivalQuarryId?.designation || '-'}
                                                </Typography>
                                            )}
                                            {isTruckContextEnabled(ride.mode, 'secondTimeOnSite') && (
                                                <Typography variant="body2" fontWeight="medium">
                                                    {' → '}
                                                    {ride.activitySite.name || '-'}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Stack>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            {ride.transfers.map((transfer) => (
                                                <Stack
                                                    key={transfer._id}
                                                    direction="column"
                                                    spacing={1}
                                                    sx={{
                                                        bgcolor: getMaterialStateColor(transfer.state),
                                                        p: 0.5,
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Typography variant="body2" color={'black'}>
                                                        {transfer.machineId?.designation ||
                                                            transfer.toolId?.designation ||
                                                            `${transfer.rentalName} ${transfer.rentalCompanyId?.designation}` ||
                                                            '-'}
                                                    </Typography>
                                                    <Typography variant="body2" color={'black'}>
                                                        {transfer.siteId?.name || transfer.quarryId?.designation || '-'}
                                                    </Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </Stack>
                                </Paper>
                            ))}
                    </Box>
                </CardContent>
            </Collapse>
        </Card>
    );
};

export default TruckCardSupervision;
